import React, { useState } from 'react';

import { Archive, ArchiveRestore, Pencil } from 'lucide-react';
import ReactModal from 'react-modal';

import { Label } from '../types';

type Props = {
  isOpen: boolean;
  labels: Label[];
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onSetArchived: (id: string, archived: boolean) => Promise<void>;
};

/**
 * The CRUD behind the label picker, same shape as MacroModal. Archive instead
 * of delete: the label leaves the picker but stays on every conversation that
 * already carries it, so there is no destructive action to confirm.
 */
const LabelModal: React.FC<Props> = ({
  isOpen,
  labels,
  onClose,
  onCreate,
  onRename,
  onSetArchived,
}: Props) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const reset = (): void => {
    setEditingId(null);
    setName('');
  };

  const toggleArchived = async (label: Label): Promise<void> => {
    try {
      await onSetArchived(label.id, !label.archived);
    } catch {
      // The caller already told them; archiving has no local state to restore.
    }
  };

  const save = async (): Promise<void> => {
    if (!name.trim() || isSaving) return;
    setIsSaving(true);
    try {
      if (editingId) await onRename(editingId, name.trim());
      else await onCreate(name.trim());
      reset();
    } catch {
      // Keep what they typed: the caller already told them the save failed,
      // and a cleared form on top of that error loses the only copy.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal"
      overlayClassName="overlay"
      appElement={document.querySelector('#root') as HTMLElement}
    >
      <h2 className="text-lg font-semibold text-black-1.0">Labels</h2>
      <p className="mt-1 text-sm text-gray-1.0">
        Shared by the whole team. Every conversation needs at least one before it can be closed.
      </p>

      <ul className="mt-4 overflow-y-auto max-h-80 divide-y divide-white-1.0">
        {labels.length === 0 && <li className="py-2 text-sm text-gray-1.0">None yet.</li>}
        {labels.map((label) => (
          <li key={label.id} className="flex items-center gap-2 py-2">
            <p
              className={
                label.archived
                  ? 'flex-1 min-w-0 text-sm truncate line-through text-gray-1.0'
                  : 'flex-1 min-w-0 text-sm truncate text-black-1.0'
              }
            >
              {label.name}
            </p>
            <button
              type="button"
              aria-label={`Rename ${label.name}`}
              className="p-1 rounded text-gray-2.0 hover:bg-white-1.0"
              onClick={() => {
                setEditingId(label.id);
                setName(label.name);
              }}
            >
              <Pencil size={16} />
            </button>
            <button
              type="button"
              aria-label={label.archived ? `Restore ${label.name}` : `Archive ${label.name}`}
              className="p-1 rounded text-gray-2.0 hover:bg-white-1.0"
              onClick={() => void toggleArchived(label)}
            >
              {label.archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
            </button>
          </li>
        ))}
      </ul>

      <div className="pt-4 mt-4 border-t border-white-2.0">
        <p className="text-sm font-medium text-black-1.0">
          {editingId ? 'Rename label' : 'New label'}
        </p>
        <input
          type="text"
          value={name}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
          placeholder="Name"
          className="w-full px-3 py-2 mt-2 border rounded border-white-3.0 focus:outline-none"
        />
        <div className="flex justify-end gap-2 mt-3">
          {editingId && (
            <button
              type="button"
              className="px-3 py-1 text-sm rounded text-gray-2.0 hover:bg-white-1.0"
              onClick={reset}
            >
              Cancel edit
            </button>
          )}
          <button
            type="button"
            className="px-3 py-1 text-sm rounded text-gray-2.0 hover:bg-white-1.0"
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="button"
            disabled={!name.trim() || isSaving}
            className="px-4 py-1 text-sm rounded bg-primary-2.0 text-white-0.0 disabled:opacity-50"
            onClick={() => void save()}
          >
            {editingId ? 'Save changes' : 'Add'}
          </button>
        </div>
      </div>
    </ReactModal>
  );
};

export default LabelModal;
